import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { useAuth } from '@/hooks/useAuth';
import { useNavigate, useLocation } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { signInSchema, signUpSchema } from '@/lib/validation';

export default function Auth() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [fullName, setFullName] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [isSignUp, setIsSignUp] = useState(false);
  
  const { signIn, signUp, user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Detect invitation flow from URL hash
  useEffect(() => {
    const hash = location.hash;
    if (hash && hash.includes('type=invite')) {
      setIsSignUp(true);
      setMessage('Please create your password to complete your invitation');
    }
  }, [location]);

  useEffect(() => {
    if (user) {
      navigate('/role-redirect');
    }
  }, [user, navigate]);

  const handleSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    
    // Validate input with Zod
    const result = signInSchema.safeParse({ email, password });
    if (!result.success) {
      setError(result.error.errors[0].message);
      setLoading(false);
      return;
    }
    
    const { error } = await signIn(result.data.email, result.data.password);
    
    if (error) {
      setError(error.message);
    } else {
      navigate('/role-redirect');
    }
    setLoading(false);
  };

  const handleSignUp = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setMessage('');
    
    // Validate input with Zod
    const result = signUpSchema.safeParse({ email, password, fullName });
    if (!result.success) {
      setError(result.error.errors[0].message);
      setLoading(false);
      return;
    }
    
    const { error } = await signUp(result.data.email, result.data.password, result.data.fullName);
    
    if (error) {
      setError(error.message);
    } else {
      setMessage('Check your email for the confirmation link!');
    }
    setLoading(false);
  };

  const handleGoogleSignIn = async () => {
    setLoading(true);
    setError('');
    
    const { error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo: `${window.location.origin}/role-redirect`
      }
    });
    
    if (error) {
      setError(error.message);
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen relative overflow-hidden flex items-center justify-center p-4 bg-gray-100">
      {/* Animated Waves Background */}
      <div className="absolute inset-0 overflow-hidden">
        {/* Wave 1 - Capa superior */}
        <div className="absolute inset-0 w-full h-full opacity-70">
          <svg className="absolute inset-0 w-full h-full" viewBox="0 0 1920 1080" preserveAspectRatio="xMidYMid slice">
            <defs>
              <linearGradient id="wave1" x1="0%" y1="0%" x2="100%" y2="0%">
                <stop offset="0%" stopColor="#ea580c" />
                <stop offset="33%" stopColor="#f97316" />
                <stop offset="66%" stopColor="#fb923c" />
                <stop offset="100%" stopColor="#fbbf24" />
              </linearGradient>
            </defs>
            <path 
              fill="url(#wave1)" 
              d="M-400,500 C320,400 420,300 740,350 C1060,400 1160,500 2400,450 L2400,1080 L-400,1080 Z"
              className="animate-[wave1_2.7s_ease-in-out_infinite]"
            />
          </svg>
        </div>

        {/* Wave 2 - Capa media */}
        <div className="absolute inset-0 w-full h-full opacity-70">
          <svg className="absolute inset-0 w-full h-full" viewBox="0 0 1920 1080" preserveAspectRatio="xMidYMid slice">
            <defs>
              <linearGradient id="wave2" x1="0%" y1="0%" x2="100%" y2="0%">
                <stop offset="0%" stopColor="#dc2626" />
                <stop offset="20%" stopColor="#ea580c" />
                <stop offset="40%" stopColor="#f97316" />
                <stop offset="60%" stopColor="#f59e0b" />
                <stop offset="100%" stopColor="#eab308" />
              </linearGradient>
            </defs>
            <path 
              fill="url(#wave2)" 
              d="M-400,650 C300,550 600,450 900,500 C1200,550 1500,650 2400,600 L2400,1080 L-400,1080 Z"
              className="animate-[wave2_3.3s_ease-in-out_infinite]"
            />
          </svg>
        </div>

        {/* Wave 3 - Capa inferior */}
        <div className="absolute inset-0 w-full h-full opacity-70">
          <svg className="absolute inset-0 w-full h-full" viewBox="0 0 1920 1080" preserveAspectRatio="xMidYMid slice">
            <defs>
              <linearGradient id="wave3" x1="0%" y1="0%" x2="100%" y2="0%">
                <stop offset="0%" stopColor="#9a3412" />
                <stop offset="17%" stopColor="#c2410c" />
                <stop offset="33%" stopColor="#dc2626" />
                <stop offset="50%" stopColor="#ea580c" />
                <stop offset="67%" stopColor="#ca8a04" />
                <stop offset="100%" stopColor="#a16207" />
              </linearGradient>
            </defs>
            <path 
              fill="url(#wave3)" 
              d="M-400,800 C400,700 800,600 1200,650 C1600,700 1800,800 2400,750 L2400,1080 L-400,1080 Z"
              className="animate-[wave3_4s_ease-in-out_infinite]"
            />
          </svg>
        </div>
      </div>

      <div className="relative z-10 w-full flex flex-col items-center">
        {/* Auth Card */}
        <div className="w-full max-w-md">
          {/* AppRemedy Logo - Centered with card content */}
          <div className="text-center mb-8">
            <h1 className="text-6xl font-black">
              <span className="bg-gradient-to-r from-orange-300 to-orange-600 bg-clip-text text-transparent">App</span>
              <span className="bg-gradient-to-b from-gray-700 to-black bg-clip-text text-transparent">Remedy</span>
            </h1>
          </div>

          <Card className="bg-white shadow-2xl border-0 rounded-2xl overflow-hidden">
            <CardContent className="p-8">
            {error && (
              <Alert variant="destructive" className="mb-6">
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            {message && (
              <Alert className="mb-6">
                <AlertDescription>{message}</AlertDescription>
              </Alert>
            )}

            {!isSignUp ? (
              // Sign In Form
              <form onSubmit={handleSignIn} className="space-y-6">
                <div className="text-center mb-6">
                  <h2 className="text-xl text-gray-800">Sign in to your account</h2>
                </div>

                <div className="space-y-4">
                  <div>
                    <Label htmlFor="email" className="text-sm font-medium text-gray-700 mb-2 block">
                      Email
                    </Label>
                    <Input
                      id="email"
                      type="email"
                      placeholder="Enter your email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                      className="w-full px-3 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-gray-50"
                    />
                  </div>

                  <div>
                    <Label htmlFor="password" className="text-sm font-medium text-gray-700 mb-2 block">
                      Password
                    </Label>
                    <Input
                      id="password"
                      type="password"
                      placeholder="Enter your password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                      className="w-full px-3 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-gray-50"
                    />
                  </div>
                </div>

                <Button 
                  type="submit" 
                  className="w-full bg-gray-800 hover:bg-gray-900 text-white py-3 rounded-lg font-medium text-base"
                  disabled={loading}
                >
                  {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  Sign In
                </Button>

                <div className="text-center">
                  <div className="relative">
                    <div className="absolute inset-0 flex items-center">
                      <div className="w-full border-t border-gray-300"></div>
                    </div>
                    <div className="relative flex justify-center text-sm">
                      <span className="px-2 bg-white text-gray-500">OR CONTINUE WITH</span>
                    </div>
                  </div>
                </div>

                <Button 
                  type="button" 
                  variant="outline"
                  onClick={handleGoogleSignIn}
                  disabled={loading}
                  className="w-full border border-gray-300 bg-white hover:bg-gray-50 text-gray-700 py-3 rounded-lg font-medium text-base flex items-center justify-center"
                >
                  <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
                    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                  </svg>
                  {loading ? 'Connecting...' : 'Continue with Gmail'}
                </Button>

                <div className="text-center">
                  <button 
                    type="button"
                    className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                  >
                    Forgot your password?
                  </button>
                </div>
              </form>
            ) : (
              // Sign Up Form
              <form onSubmit={handleSignUp} className="space-y-6">
                <div className="text-center mb-6">
                  <h2 className="text-xl font-semibold text-gray-800">Create your account</h2>
                </div>

                <div className="space-y-4">
                  <div>
                    <Label htmlFor="fullName" className="text-sm font-medium text-gray-700 mb-2 block">
                      Full Name
                    </Label>
                    <Input
                      id="fullName"
                      type="text"
                      placeholder="Enter your full name"
                      value={fullName}
                      onChange={(e) => setFullName(e.target.value)}
                      required
                      className="w-full px-3 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-gray-50"
                    />
                  </div>

                  <div>
                    <Label htmlFor="signup-email" className="text-sm font-medium text-gray-700 mb-2 block">
                      Email
                    </Label>
                    <Input
                      id="signup-email"
                      type="email"
                      placeholder="Enter your email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                      className="w-full px-3 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-gray-50"
                    />
                  </div>

                  <div>
                    <Label htmlFor="signup-password" className="text-sm font-medium text-gray-700 mb-2 block">
                      Password
                    </Label>
                    <Input
                      id="signup-password"
                      type="password"
                      placeholder="Enter your password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                      minLength={6}
                      className="w-full px-3 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-gray-50"
                    />
                  </div>
                </div>

                <Button 
                  type="submit" 
                  className="w-full bg-gray-800 hover:bg-gray-900 text-white py-3 rounded-lg font-medium text-base"
                  disabled={loading}
                >
                  {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  Create Account
                </Button>

                <div className="text-center">
                  <p className="text-sm text-gray-600">
                    Already have an account?{' '}
                    <button
                      type="button"
                      onClick={() => setIsSignUp(false)}
                      className="text-blue-600 hover:text-blue-800 font-medium"
                    >
                      Sign in
                    </button>
                  </p>
                </div>
              </form>
            )}
           </CardContent>
         </Card>
        </div>
      </div>
    </div>
  );
}